/**
 * One decode per task, shared by every waveform lane on screen.
 *
 * WHY THIS IS A MODULE-LEVEL CACHE AND NOT COMPONENT STATE
 * -------------------------------------------------------
 * `WaveTrimmer` decodes on open and drops on close, which is right for a modal
 * editor you use a few times a session. The lane is different: it is ALWAYS
 * visible on Verify, and the Correct tab can have thirty of them on one screen.
 * Decoding per component would decode the same 73 s file thirty times and hold
 * thirty copies of the PCM — on a phone that is the difference between a
 * scrolling list and a crash.
 *
 * So: exactly ONE decoded task at a time, ref-counted by the hooks below. When
 * the last lane using a task unmounts (a tab switch, a new task), the buffer and
 * every derived analysis go with it. Two tasks' worth of decoded PCM is tens of
 * megabytes and this is a phone.
 *
 * Nothing here writes. The segment cache is invalidated wholesale on any db
 * change, because the writes that matter (a drag applying, a trim, a removal)
 * all move boundaries the lane is drawing.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT THE ONLY DECODED BUFFER IN THE APP
 * ---------------------------------------------------------------------------
 * Four places hold a decoded `AudioBuffer`, each with a DIFFERENT lifecycle.
 * They are deliberately separate; merging them would tie a component's mount
 * to an edit engine's working set. Know which one you are looking at:
 *
 *   1. HERE — one task, ref-counted by lane mount/unmount. Freed when the last
 *      lane using it unmounts. Owns the derived onset/envelope analyses too.
 *   2. `services/decodedAudio.ts` — one slot, for the EDIT ENGINES (cutOps,
 *      boundaryEdits, segmentEdits). A second task evicts the first. Its
 *      invalidation hook has no callers yet — follow-up F11.
 *   3. `WaveTrimmer.tsx` — decodes on open, drops on close. Right for a modal
 *      you use a few times a session; consuming `useTaskAudio` instead is
 *      follow-up F4.
 *   4. `pages/CutsPage.tsx` — a `Map<taskId, AudioBuffer>` in React state.
 *      NOTE: it is never evicted while the page stays mounted, so working
 *      through a Cuts queue that spans several recordings accumulates one
 *      decoded PCM copy per recording. Bounded by the page's lifetime, not by
 *      memory. Not changed in the restructure (behavior); see ARCHITECTURE.md.
 *
 * `services/firstPass.ts` also decodes, but transiently during a run — it holds
 * no cache and is not part of this census.
 */

import { getSegmentsByTask, getTaskAudio, subscribeToDb } from '../db';
import { useEffect, useState } from 'react';
import { decodeAudioBlob, extractMono } from '../services/audioSlicer';
import { detectOnsets } from '../services/onsetDetect';
import { computeSpeechEnvelope } from '../services/vad';
import type { Segment } from '../types';

/* ------------------------------------------------------------------ */
/* Decoded audio                                                       */
/* ------------------------------------------------------------------ */

export interface TaskAudio {
  taskId: string;
  /** The decoded ORIGINAL file. Slice windows out of this, never store copies. */
  buffer: AudioBuffer;
  sampleRate: number;
  durationSec: number;
}

let entry: TaskAudio | null = null;
let pending: { taskId: string; promise: Promise<TaskAudio> } | null = null;
/** How many mounted lanes are using each task. */
const users = new Map<string, number>();

/** The decoded audio for `taskId` if it is already in hand, else null. */
export function peekTaskAudio(taskId: string): TaskAudio | null {
  return entry && entry.taskId === taskId ? entry : null;
}

/** Decode `taskId`'s original audio, reusing the cached decode when possible. */
export function loadTaskAudio(taskId: string): Promise<TaskAudio> {
  const hit = peekTaskAudio(taskId);
  if (hit) return Promise.resolve(hit);
  if (pending && pending.taskId === taskId) return pending.promise;

  const promise = (async () => {
    const blob = await getTaskAudio(taskId);
    if (!blob) throw new Error('The original audio for this task is no longer stored.');
    const buffer = await decodeAudioBlob(blob);
    // Single slot: adopting this decode releases the previous task's.
    entry = { taskId, buffer, sampleRate: buffer.sampleRate, durationSec: buffer.duration };
    snapCache.clear();
    return entry;
  })();

  pending = { taskId, promise };
  void promise.catch(() => {
    if (pending && pending.promise === promise) pending = null;
  });
  return promise;
}

/** Drop the decode (and everything derived from it) for a task, or for all. */
export function releaseTaskAudio(taskId?: string): void {
  if (taskId && entry && entry.taskId !== taskId) return;
  entry = null;
  pending = null;
  snapCache.clear();
}

function retain(taskId: string): void {
  users.set(taskId, (users.get(taskId) ?? 0) + 1);
}

function release(taskId: string): void {
  const next = (users.get(taskId) ?? 1) - 1;
  if (next > 0) {
    users.set(taskId, next);
    return;
  }
  users.delete(taskId);
  // Nothing on screen needs this file any more — give the PCM back.
  releaseTaskAudio(taskId);
}

export interface TaskAudioState {
  audio: TaskAudio | null;
  loading: boolean;
  error: string | null;
}

/**
 * Decoded audio for a task, ref-counted.
 *
 * Pass `enabled: false` for a lane that is collapsed to a strip: a strip draws
 * segment tiles only, so it must not pay for a decode it will never show.
 */
export function useTaskAudio(taskId: string | null, enabled = true): TaskAudioState {
  const [state, setState] = useState<TaskAudioState>(() => {
    const hit = taskId ? peekTaskAudio(taskId) : null;
    return { audio: hit, loading: false, error: null };
  });

  useEffect(() => {
    if (!taskId || !enabled) {
      setState({ audio: null, loading: false, error: null });
      return;
    }
    const hit = peekTaskAudio(taskId);
    setState(hit ? { audio: hit, loading: false, error: null } : { audio: null, loading: true, error: null });

    retain(taskId);
    let cancelled = false;
    void loadTaskAudio(taskId)
      .then((a) => {
        if (!cancelled) setState({ audio: a, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          audio: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not decode this task audio.',
        });
      });

    return () => {
      cancelled = true;
      release(taskId);
    };
  }, [taskId, enabled]);

  return state;
}

/* ------------------------------------------------------------------ */
/* Snap targets                                                        */
/* ------------------------------------------------------------------ */

/** Two landmarks closer than this are the same landmark. */
const SNAP_DEDUPE_SEC = 0.015;

const snapCache = new Map<string, Float64Array>();

/**
 * Places inside `[fromSec, toSec]` where a word could plausibly start.
 *
 * Two independent kinds of evidence, deliberately pooled rather than ranked:
 *  - SPECTRAL ONSETS (`detectOnsets`) — the transient at the start of a
 *    consonant. Precise, and the thing a human is actually aiming at.
 *  - SPEECH RISES — the frames where the VAD mask turns on. Coarser (5 ms
 *    hop), but it catches a soft vowel-initial word that produces no transient
 *    at all, which is exactly the case where the onset detector is silent.
 *
 * Computed over the WINDOW, not the file: the analysis is ~50 ms for seven
 * seconds of audio and minutes for a long recording, and a magnet only has to
 * know about landmarks you can drag to. Cached per window, dropped with the
 * decode.
 */
export function snapTargetsFor(audio: TaskAudio, fromSec: number, toSec: number): Float64Array {
  const from = Math.max(0, Math.min(audio.durationSec, fromSec));
  const to = Math.max(from, Math.min(audio.durationSec, toSec));
  const key = `${audio.taskId}|${from.toFixed(2)}|${to.toFixed(2)}`;
  const hit = snapCache.get(key);
  if (hit) return hit;
  if (to - from < 0.05) {
    const empty = new Float64Array(0);
    snapCache.set(key, empty);
    return empty;
  }

  const mono = extractMono(audio.buffer, from, to);
  const raw: number[] = [];
  try {
    for (const o of detectOnsets(mono, audio.sampleRate).onsets) raw.push(from + o.timeSec);
  } catch {
    // A magnet is a nicety; a failed analysis must not break dragging.
  }
  try {
    const env = computeSpeechEnvelope(mono, audio.sampleRate);
    for (let i = 1; i < env.mask.length; i++) {
      if (env.mask[i] && !env.mask[i - 1]) raw.push(from + i * env.hopSec);
    }
  } catch {
    /* same */
  }

  raw.sort((a, b) => a - b);
  const out: number[] = [];
  for (const t of raw) {
    if (out.length === 0 || t - out[out.length - 1] > SNAP_DEDUPE_SEC) out.push(t);
  }
  const packed = Float64Array.from(out);
  // One window per open lane at most; a handful of entries, all dropped with
  // the decode they were derived from.
  if (snapCache.size > 12) snapCache.clear();
  snapCache.set(key, packed);
  return packed;
}

/* ------------------------------------------------------------------ */
/* Segments of a task                                                  */
/* ------------------------------------------------------------------ */

const segCache = new Map<string, Segment[]>();
const segInflight = new Map<string, Promise<Segment[]>>();
const watchers = new Set<() => void>();
let generation = 0;

subscribeToDb(() => {
  segCache.clear();
  segInflight.clear();
  generation += 1;
  for (const w of watchers) {
    try {
      w();
    } catch {
      /* a broken watcher must not take a write down with it */
    }
  }
});

function fetchSegments(taskId: string): Promise<Segment[]> {
  const hit = segCache.get(taskId);
  if (hit) return Promise.resolve(hit);
  const running = segInflight.get(taskId);
  if (running) return running;
  const p = getSegmentsByTask(taskId).then(
    (rows) => {
      segCache.set(taskId, rows);
      segInflight.delete(taskId);
      return rows;
    },
    (err: unknown) => {
      segInflight.delete(taskId);
      throw err;
    },
  );
  segInflight.set(taskId, p);
  return p;
}

/**
 * Every segment of a task, in time order, re-read after any write.
 *
 * The lane needs ALL of a task's rows, not the queue the page happens to be
 * holding: the Verify queue is only the pending ones and the Correct queue only
 * the incorrect ones, so drawing from either would show a lane full of holes and
 * compute a ripple against neighbours that are not really there.
 */
export function useTaskSegments(taskId: string | null): Segment[] {
  const [gen, setGen] = useState(generation);
  const [rows, setRows] = useState<Segment[]>(() => (taskId ? segCache.get(taskId) ?? [] : []));

  useEffect(() => {
    const w = () => setGen(generation);
    watchers.add(w);
    return () => {
      watchers.delete(w);
    };
  }, []);

  useEffect(() => {
    if (!taskId) {
      setRows([]);
      return;
    }
    const hit = segCache.get(taskId);
    // Same array identity on a cache hit, so React bails out of the re-render.
    if (hit) {
      setRows(hit);
      return;
    }
    let cancelled = false;
    void fetchSegments(taskId)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, gen]);

  return rows;
}
